import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert"
import { cn } from "../lib/utils"

export function AlertRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("alert")
  const resolvedVariantRaw = element.variant ?? atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const variant = resolvedVariantRaw as "default" | "destructive"

  // Compound mode: children are alert-title, alert-description
  const hasCompoundChildren = element.children?.some(
    (c) => c.type === "alert-title" || c.type === "alert-description"
  )

  if (hasCompoundChildren) {
    return (
      <Alert variant={variant} className={cn(element.className)} data-testid={`alert-${element.id}`} data-atom-type="alert" data-variant={resolvedVariantRaw} data-size={resolvedSize}>
        {element.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </Alert>
    )
  }

  // Legacy mode
  const title = element.text ? String(resolveSymbol(element.text) ?? "") : ""
  const description = element.children?.[0]?.text
    ? String(resolveSymbol(element.children[0].text) ?? "")
    : ""

  return (
    <Alert variant={variant} className={cn(element.className)} data-testid={`alert-${element.id}`} data-atom-type="alert" data-variant={resolvedVariantRaw} data-size={resolvedSize}>
      {title && <AlertTitle>{title}</AlertTitle>}
      {description && <AlertDescription>{description}</AlertDescription>}
    </Alert>
  )
}
